import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  getNotifications, getUnreadCount,
  markRead, markAllRead,
  deleteNotification, clearAll,
} from "../api/notifications";

const Ctx = createContext(null);

// ── Sound helper ─────────────────────────────────────────────────────────────
function shouldPlayChime() {
  try {
    return localStorage.getItem("dd_notification_sound_enabled") === "true";
  } catch {
    return false;
  }
}

function playNotificationChime() {
  if (!shouldPlayChime()) return;
  try {
    const audio = new Audio("/notification-chime.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch {
    // Audio playback failures are non-fatal
  }
}

export function NotificationsProvider({ children }) {
  const { user }                                  = useAuth();
  const [notifications, setNotifications]         = useState([]);
  const [unreadCount, setUnreadCount]             = useState(0);
  const [loading, setLoading]                     = useState(false);
  const [trayOpen, setTrayOpen]                   = useState(false);
  const pollRef                                   = useRef(null);
  const prevUnreadRef                             = useRef(0);

  // ── Load full list ─────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const items = await getNotifications();
      setNotifications(items);
      setUnreadCount(items.filter((n) => !n.read).length);
    } catch (_) {}
    finally { setLoading(false); }
  }, [user]);

  // ── Lightweight unread poll (every 60 s) ───────────────────────────────────
  const pollUnread = useCallback(async () => {
    if (!user) return;
    try {
      const count = await getUnreadCount();
      // Play chime if new notifications arrived (likely job_complete)
      if (count > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        playNotificationChime();
      }
      prevUnreadRef.current = count;
      setUnreadCount(count);
    } catch (_) {}
  }, [user]);

  useEffect(() => {
    if (!user) return;
    pollUnread();
    pollRef.current = setInterval(pollUnread, 60_000);
    return () => clearInterval(pollRef.current);
  }, [user, pollUnread]);

  // Load full list when tray opens
  useEffect(() => { if (trayOpen) refresh(); }, [trayOpen, refresh]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleMarkRead = useCallback(async (id) => {
    try {
      await markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) {}
  }, []);

  const handleDelete = useCallback(async (id) => {
    const wasUnread = notifications.find((n) => n.id === id)?.read === false;
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  }, [notifications]);

  const handleClearAll = useCallback(async () => {
    try {
      await clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (_) {}
  }, []);

  return (
    <Ctx.Provider value={{
      notifications, unreadCount, loading,
      trayOpen, setTrayOpen,
      refresh,
      markRead:    handleMarkRead,
      markAllRead: handleMarkAllRead,
      deleteNotification: handleDelete,
      clearAll:    handleClearAll,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationsProvider");
  return ctx;
}
